import { useQueries, useQuery } from "@tanstack/react-query";

import { getSoilSightScenario } from "services";

import { useIsAuthenticated } from "utils";

import { GET_SOIL_SIGHT_SCENARIO } from "consts";

import { SoilSightScenarioDetail } from "models";

const STALE_MS = 1000 * 60 * 5;

export const soilSightScenarioDetailQueryOptions = (scenarioId: string) => ({
  queryKey: [GET_SOIL_SIGHT_SCENARIO, scenarioId],
  queryFn: () => getSoilSightScenario(scenarioId),
  staleTime: STALE_MS,
});

export type UseGetSoilSightScenarioDetailsOptions = {
  enabled?: boolean; // When false, no per-id fetches run. Omit to use the default auth gate (`user` + `token`).
};

// handling multiple scenarios with function overloading

// One scenario id
export function useGetSoilSightScenarioDetails(
  scenarioId: string,
  options?: UseGetSoilSightScenarioDetailsOptions
): { data: SoilSightScenarioDetail | undefined; loading: boolean; error: boolean; firstError: unknown };

// Many ids
export function useGetSoilSightScenarioDetails(
  scenarioIds: readonly string[],
  options?: UseGetSoilSightScenarioDetailsOptions
): { data: SoilSightScenarioDetail[]; loading: boolean; error: boolean; firstError: unknown };

export function useGetSoilSightScenarioDetails(
  scenarioIdOrIds: string | readonly string[],
  options?: UseGetSoilSightScenarioDetailsOptions
): { data: SoilSightScenarioDetail | undefined | SoilSightScenarioDetail[]; loading: boolean; error: boolean; firstError: unknown } {
  const isAuthenticated = useIsAuthenticated();
  const enabledFlag = options?.enabled ?? isAuthenticated;

  const isSingle = typeof scenarioIdOrIds === "string";
  const ids = isSingle ? [scenarioIdOrIds] : scenarioIdOrIds;

  // combine's result is structurally shared: same reference until the data changes, new one after a refetch brings new data.
  const {
    data: batchData,
    loading,
    error,
    firstError,
  } = useQueries({
    queries: ids.map((id) => ({
      ...soilSightScenarioDetailQueryOptions(id),
      enabled: enabledFlag && !!id,
    })),
    combine: (results) => ({
      data: results.map((q) => (q.isSuccess && q.data ? q.data : null)).filter((d): d is SoilSightScenarioDetail => d !== null),
      loading: results.some((q) => q.isLoading),
      error: results.some((q) => q.isError),
      firstError: results.find((q) => q.isError)?.error ?? null,
    }),
  });

  if (isSingle) {
    return { data: batchData[0], loading, error, firstError };
  }

  return { data: batchData, loading, error, firstError };
}

export const useGetSoilSightScenario = (scenarioId: string) => {
  const isAuthenticated = useIsAuthenticated();

  return useQuery<SoilSightScenarioDetail>({
    ...soilSightScenarioDetailQueryOptions(scenarioId),
    enabled: isAuthenticated && !!scenarioId,
  });
};
